/**
 * Board Forms API — admin CRUD + the public capture endpoints.
 * Mirrors server/src/routes/forms.ts.
 */
import { apiRequest } from './core.js';
import type { ItemResponse } from '../../types/api.js';

/**
 * A field's semantic job beyond "collect this value" (server: FormFieldRole).
 *
 * The name roles let a form say which field holds the person's name, so the
 * submitted value reaches `Customer.firstName` instead of only the board cell —
 * which is why a form lead used to show up everywhere as a raw email address.
 * Keep in step with `server/src/services/boards/boardFormFields.ts`.
 */
export type FormFieldRole = 'EMAIL_CONSENT' | 'FULL_NAME' | 'FIRST_NAME' | 'LAST_NAME';

/**
 * ADMIN field shape — what the authenticated form builder sends and reads back.
 * Keyed by `columnId` because the merchant is choosing board columns.
 * `key` is the server-assigned opaque public handle; it comes back on reads and
 * is preserved (never invented) on writes.
 */
export interface FormField {
  columnId: string;
  label: string;
  required: boolean;
  key?: string;
  role?: FormFieldRole;
}

/**
 * PUBLIC field shape — what GET /forms/:slug actually puts on the wire.
 *
 * Deliberately NOT `extends FormField`: the public payload carries NO columnId.
 * The server refuses to leak the internal ID space across an unauthenticated
 * boundary (boardFormFields.ts, "Deliberately has NO columnId (D1)"), so the
 * only identity a visitor's browser ever sees — and the only key
 * POST /forms/:slug/submit accepts in `values` — is the opaque `key`.
 * Extending the admin type is what made the compiler certify a shape the
 * server never sends.
 */
export interface PublicFormField {
  key: string;
  label: string;
  required: boolean;
  type: string;
  role?: FormFieldRole;
}

export interface PublicForm {
  slug: string;
  title: string;
  description: string | null;
  fields: PublicFormField[];
  honeypotField: string;
}

export interface BoardForm {
  id: string;
  workspaceId: string;
  boardId: string;
  groupId: string | null;
  title: string;
  description: string | null;
  slug: string;
  enabled: boolean;
  fields: FormField[];
  submitCount: number;
  createdAt: string;
}

export interface CreateFormBody {
  boardId: string;
  groupId?: string;
  title: string;
  description?: string;
  fields: FormField[];
}

/**
 * The visitor's acquisition context, sent alongside a public submission.
 *
 * The keys are the QUERY-STRING names, not camelCase, and that is deliberate:
 * the page copies them straight out of `location.search` and the server's
 * `attributionSchema` (routes/forms.ts) is `.strict()` on exactly these names,
 * so there is no rename step between the two hand-maintained types. A rename
 * step is what let this page ship keyed off a `columnId` the server never sent.
 *
 * `landing_url` is the full href including the query string — which is how a
 * `gclid` reaches `AttributionTouch.landingUrl`, since that model has an
 * `fbclid` column but no `gclid` one.
 */
export interface PublicFormAttribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  gclid?: string;
  fbclid?: string;
  referrer?: string;
  landing_url?: string;
}

export interface SubmitResult {
  ok: boolean;
  dropped: boolean;
  boardItemId?: string;
}

export const formsApi = {
  // ----- admin -----
  create: (body: CreateFormBody) =>
    apiRequest<ItemResponse<BoardForm>>(`/forms`, { method: 'POST', body: JSON.stringify(body) }),
  list: (boardId: string) =>
    apiRequest<ItemResponse<BoardForm[]>>(`/forms?boardId=${encodeURIComponent(boardId)}`),
  update: (id: string, patch: Partial<Omit<CreateFormBody, 'boardId'>> & { enabled?: boolean }) =>
    apiRequest<ItemResponse<BoardForm>>(`/forms/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  remove: (id: string) =>
    apiRequest<ItemResponse<{ deleted: boolean }>>(`/forms/${id}`, { method: 'DELETE' }),

  // ----- public (no auth) -----
  getPublic: (slug: string) => apiRequest<ItemResponse<PublicForm>>(`/forms/${encodeURIComponent(slug)}`),
  /**
   * `values` is keyed by PublicFormField.key (plus PublicForm.honeypotField) — never by columnId.
   *
   * `attribution` travels as its OWN top-level object, never inside `values`:
   * the server checks every key of `values` against this form's field keys and
   * answers an unknown one with a 400, so smuggling UTM in there would reject
   * the whole submission.
   */
  submit: (slug: string, values: Record<string, unknown>, attribution?: PublicFormAttribution) =>
    apiRequest<ItemResponse<SubmitResult>>(`/forms/${encodeURIComponent(slug)}/submit`, {
      method: 'POST',
      body: JSON.stringify(attribution ? { values, attribution } : { values }),
    }),
};
